import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import { Table, Space, Card, Row, Col, Input, Checkbox, message, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { API_URL } from "../../url_config";
import {NavigationStepsAll} from "../../components/navbars/navbar_all"
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";

import { Button } from "antd";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTableMultiOtTag from "../../components/counter_multi_ottag";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne pour les zones de texte ===
function TableTetraBus({ record, onChange }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  // Synchroniser si record change
  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = (field, value) => {
    onChange(record.key, field, value);
  };

  return (
    <div style={{ padding: "20px" }}>


      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={5}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={5}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const TetraBusAll = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isMountedRef = useRef(true);
  const pendingUpdatesRef = useRef(new Map()); // Map: "id_field" -> {id, field, value, timestamp}
  const timeoutRefs = useRef(new Map()); // Map: "id_field" -> timeoutId

  // Fonction pour charger les données
  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(`${API_URL}/validations/tetra_bus/?ot_tag=all`);
      if (!isMountedRef.current) return;

        const formatted = res.data
          .map((item) => ({
            id: item.id,
            ot_tag: item.ot_tag,
            poste: item.poste,
            affichage_ihm: item.affichage_ihm,
            presence_des_ligne: item.presence_des_ligne,
            presence_des_coquilles: item.presence_des_coquilles,
            appel_individuel: item.appel_individuel,
            appel_general: item.appel_general,
            reception_d_un_appel_mr: item.reception_d_un_appel_mr,
            comment: item.comment,
            aide: item.aide,
            signal: item.signal,
          }))
          .sort((a, b) => {
            // Tri par poste puis par id
            if (a.poste !== b.poste) {
              return (a.poste || "").toString().localeCompare((b.poste || "").toString());
            }
            return (a.id || 0) - (b.id || 0);
          })
          .map((item, index) => ({
            ...item,
            key: index,
          }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      }
  }, []);

  // Rafraîchissement manuel avec loading
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Charger les données au montage uniquement
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchData().finally(() => setLoading(false));

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  // Fonction pour envoyer une mise à jour
  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;

    try {
      await apiClient.patch(
        `${API_URL}/validations/tetra_bus/${update.id}/`,
        { [update.field]: update.value }
      );
      // Supprimer uniquement si la valeur n'a pas changé depuis
      const current = pendingUpdatesRef.current.get(updateKey);
      if (current && current.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  };

  // Mise à jour locale + serveur
  const handleDataChange = (key, field, value) => {
    let itemId;
    setData((prev) => {
      const updated = prev.map((item) => {
        if (item.key === key) {
          itemId = item.id;
          return { ...item, [field]: value };
        }
        return item;
      });
      return updated;
    });

    if (itemId) {
      const updateKey = `${itemId}_${field}`;
      
      // Annuler le timeout précédent pour ce champ
      const existingTimeout = timeoutRefs.current.get(updateKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      // Stocker la nouvelle valeur avec un timestamp
      pendingUpdatesRef.current.set(updateKey, { 
        id: itemId, 
        field, 
        value,
        timestamp: Date.now()
      });
      
      // Programmer l'envoi dans 1 seconde
      const timeoutId = setTimeout(() => {
        sendUpdate(updateKey);
        timeoutRefs.current.delete(updateKey);
      }, 1000);
      
      timeoutRefs.current.set(updateKey, timeoutId);
    }
  };

  // Filtrer les données selon la recherche
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.poste?.toString().toLowerCase() || "").includes(search) ||
      (item.comment?.toString().toLowerCase() || "").includes(search) ||
      (item.aide?.toString().toLowerCase() || "").includes(search)
    );
  });

  const columns = [
    { 
      title: "Poste / Ligne", 
      dataIndex: "poste",
      sorter: (a, b) => (a.poste || "").localeCompare(b.poste || ""),
    },
    {
      title: "Affichage IHM",
      dataIndex: "affichage_ihm",
      sorter: (a, b) => (a.affichage_ihm === b.affichage_ihm ? 0 : a.affichage_ihm ? -1 : 1),
      render: (val, record) => {
        if (record.ot_tag === "keolis_noc") return null;
        return (
          <Checkbox
            checked={val}
            onChange={(e) =>
              handleDataChange(record.key, "affichage_ihm", e.target.checked)
            }
            className="custom-checkbox-border"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto",
              transform: "scale(1.2)",
              cursor: "pointer",
              accentColor: "#007bff",
            }}
          />
        );
      },
    },
    {
      title: "Présence des lignes",
      dataIndex: "presence_des_ligne",
      sorter: (a, b) => (a.presence_des_ligne === b.presence_des_ligne ? 0 : a.presence_des_ligne ? -1 : 1),
      render: (val, record) => {
        if (record.ot_tag === "keolis_noc") return null;
        return (
          <Checkbox
            checked={val}
            onChange={(e) =>
              handleDataChange(record.key, "presence_des_ligne", e.target.checked)
            }
            className="custom-checkbox-border"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto",
              transform: "scale(1.2)",
              cursor: "pointer",
              accentColor: "#007bff",
            }}
          />
        );
      },
    },
    {
      title: "Présence des coquilles",
      dataIndex: "presence_des_coquilles",
      sorter: (a, b) => (a.presence_des_coquilles === b.presence_des_coquilles ? 0 : a.presence_des_coquilles ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "presence_des_coquilles", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Appel ligne",
      dataIndex: "appel_general",
      sorter: (a, b) => (a.appel_general === b.appel_general ? 0 : a.appel_general ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "appel_general", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Appel individuel",
      dataIndex: "appel_individuel",
      sorter: (a, b) => (a.appel_individuel === b.appel_individuel ? 0 : a.appel_individuel ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "appel_individuel", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Réception appel MR",
      dataIndex: "reception_d_un_appel_mr",
      sorter: (a, b) => (a.reception_d_un_appel_mr === b.reception_d_un_appel_mr ? 0 : a.reception_d_un_appel_mr ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "reception_d_un_appel_mr", e.target.checked)
          }
          className="custom-checkbox-border"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.2)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
  title: "ETAT",
  dataIndex: "signal",
  sorter: (a, b) => a.signal - b.signal,
  render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
  },
},

  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsAll selectedKey="tetra_bus" />
            <CoquillesTableMultiOtTag 
              baseUrl={`${API_URL}/validations/counter/tetra_bus/`} 
              labelAll="Nb PO TETRA DSP"
              labelOk="Nb PO TETRA OK"
              labelNok="Nb PO TETRA KO"
            />
      <Input
        placeholder="Rechercher dans toutes les colonnes..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ 
          marginBottom: "24px", 
          width: "100%",
          maxWidth: "500px"
        }}
        allowClear
      />

      <Tooltip title="Rafraîchir les données">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<ReloadOutlined style={{ fontSize: "20px" }} />}
          onClick={handleManualRefresh}
          loading={refreshing}
          className="app-refresh-button"
          style={{
            zIndex: 9999,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)",
            background: "linear-gradient(135deg, #40a9ff 0%, #1890ff 100%)",
            border: "none",
            width: "56px",
            height: "56px"
          }}
        />
      </Tooltip>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TableTetraBus
              record={record}
              onChange={handleDataChange}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />


      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/tetra_bus/`}
              ot_tag="cap"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "Poste", dataIndex: "poste", key: "poste" },
              ]}
              formFields={[
                { name: "poste", label: "Poste", placeholder: "Entrez le poste" },
              ]}
              onRefresh={fetchData}
            />
      </div>

    </div>
  );
};

export default TetraBusAll;
